import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { ArrowRight, ShieldCheck, Eye, EyeOff } from "lucide-react";
import SuritLogo from "../components/brand/SuritLogo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // TEN.4: al volver de fijar la contraseña con el enlace, el correo llega por `state` de la
  // navegacion -- nunca por la URL, que queda en el historial y en los logs.
  const [email, setEmail] = useState(loc.state?.email || "");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido a Surit Clínicas");
      const target = loc.state?.from || "/app";
      nav(target, { replace: true });
    } catch (e) {
      const m = formatApiError(e.response?.data?.detail);
      setErr(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col p-8 sm:p-12 lg:p-16 bg-white">
        <Link to="/" className="flex items-center w-fit" data-testid="login-logo">
          <SuritLogo size={42} tone="claro" rotulo label="Surit.ai Clínicas" />
        </Link>

        <div className="my-auto max-w-md w-full">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">Inicia sesión</h1>
          <p className="text-slate-600 mt-2">Entra al panel de tu clínica.</p>
          {loc.state?.passwordCreada && (
            <div className="mt-4 text-sm rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2" data-testid="login-password-creada">
              Tu contraseña quedó creada. Entra con ella.
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Correo electrónico</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="login-email-input"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Contraseña</label>
              <div className="mt-1 relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white pl-3 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="login-password-input"
                  required
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 p-1" data-testid="login-toggle-password">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* RC.1: el correo ya escrito viaja por `state`, nunca por la URL. */}
              <Link to="/recuperar" state={{ email }} className="inline-block mt-2 text-xs text-blue-700 hover:underline" data-testid="login-olvide">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-electric text-white font-medium px-4 py-3 rounded-xl hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? "Iniciando…" : (<>Entrar al panel <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-6 text-xs text-slate-500 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Conexión cifrada con HTTPS · la sesión caduca sola
          </div>
        </div>

        <div className="text-xs text-slate-400 mt-8">© {new Date().getFullYear()} Surit.ai</div>
        <div className="text-xs text-slate-400 mt-2 flex gap-4">
          <a href="https://surit.ai/seguridad" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Seguridad</a>
          <a href="https://surit.ai/privacidad" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Privacidad</a>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:flex relative bg-brand-deep text-white p-12 flex-col overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10" />
        <div className="hero-blob" />
        <div className="hero-blob-2" />
        {/* J1-B (17 Ago 2026): aqui vivia un testimonio con etiqueta "CASO REAL" -- una cifra
            de resultado, el nombre de una persona y un cargo. Los tres inventados: no hay ningun
            cliente real todavia, y clinic-altura es la cuenta de trabajo interna, no una clinica
            cliente. Lo mas grave no era la cifra: el nombre correspondia a una doctora del SEED DE
            DATOS (doc-mariana), asi que el testimonio le atribuia una declaracion que nunca hizo y
            un cargo que no tiene a un registro que existe de verdad en la base.
            Se ELIMINO, no se reescribio con otro nombre ni se marco como ejemplo. Regla del
            proyecto: ninguna cifra de resultado, testimonio ni nombre de persona va en copy
            publico sin un cliente real que lo haya autorizado por escrito. Ver J1-B en CLAUDE.md
            para el detalle completo -- deliberadamente NO se reproduce aqui el texto inventado,
            para que no vuelva a aparecer en un grep ni en el bundle.
            Lo que queda es una descripcion del producto, sin numeros y sin personas. */}
        <div className="relative mt-auto max-w-md">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-300">Surit Clínicas</div>
          <p className="font-display text-3xl font-bold tracking-tight mt-3 leading-tight">
            La recepción de tu clínica, sin que nadie tenga que estar pendiente del teléfono.
          </p>
          <p className="text-sm text-slate-300 mt-5 leading-relaxed">
            Sofi contesta las dudas de tus pacientes, agenda contra la disponibilidad real de cada
            doctor y deja todo registrado en el mismo panel donde trabaja tu equipo.
          </p>
        </div>
      </div>
    </div>
  );
}
